import { DriveFolderUpload } from "@mui/icons-material";
import { Box, Collapse, Divider, FormLabel, Stack, TextField, Typography } from "@mui/material";
import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import * as TinyMCETypes from "tinymce";
import { ErrorMessage, RequiredIndicator } from "../../../../App/Components/Forms/formHelpers";
import { TemplateFieldType } from "../../../../App/Enums/TemplateFieldType";
import {
  useMessageInputHandlers,
  useMessageInputState,
} from "../../../../App/State/Hooks/MessageDialog/useMessageInput";
import { IEmailInput } from "../../../../App/State/Slices/MessageDialogInputSlice";
import { maxEmailBodyLength, maxSubjectLength } from "../../../Constants/MessageConstants";
import { Functions } from "../../../EmailComponents/Functions";
import { Plugins } from "../../../EmailComponents/Plugins";
import { TinyEditorUncontrolled } from "../../../EmailComponents/TinyEditor";
import { Toolbar } from "../../../EmailComponents/Toolbar";
import { ValidateTagsAndRecipientsResult } from "../../../MessageDialog/Components/Hooks/useValidateTagsAndRecipients";
import { MemoEmailTemplateListDialog } from "../../../MessageTemplates/EmailTemplateListDialog";
import MessageTemplateFields from "../MessageTemplateFields/MessageTemplateFields";

export interface IEmailMessageInputProps {
  disableInput?: boolean;
  editorRef: MutableRefObject<TinyMCETypes.Editor | null>;
  validation?: ValidateTagsAndRecipientsResult;
}

export default function EmailMessageInput({
  disableInput = false,
  editorRef,
  validation,
}: IEmailMessageInputProps) {
  const inputState = useMessageInputState();

  const getValidationError = (field: "subject" | "body") => {
    const inSubject = field === "subject";
    const inBody = field === "body";

    if (
      validation?.hasTitleError &&
      ((inSubject && validation.hasTitleInSubject) || (inBody && validation.hasTitleInBody))
    ) {
      return validation.recruiterTitleError;
    }
    if (
      validation?.hasAreaOfInterestError &&
      ((inSubject && validation.hasAreaOfInterestInSubject) || (inBody && validation.hasAreaOfInterestInBody))
    ) {
      return validation.areaOfInterestError;
    }
    return undefined;
  };

  const subjectErrorMessage = [inputState.subjectErrorMessage, getValidationError("subject")]
    .filter(Boolean)
    .join(" | ");

  const bodyErrorMessage = [inputState.bodyErrorMessage, getValidationError("body")]
    .filter(Boolean)
    .join(" | ");

  const [bodyTextOnly, setBodyText] = useState<string>("");
  const isProgrammaticChange = useRef(false);
  const { handleInputEmailChange, handleInputSubjectChange } = useMessageInputHandlers();

  const onEditorChange = useCallback(
    (newText: string) => {
      // Skip dispatch for programmatic changes (post-send clear, template load)
      // to avoid re-setting touched and triggering validation on an empty body
      if (!editorRef.current || isProgrammaticChange.current) {
        return;
      }
      const bodyText = editorRef.current.getContent({ format: "text" }).trim();
      setBodyText(bodyText);
      // Raw HTML is used for the actual message body which is sent to the server
      const input: IEmailInput = {
        body: newText,
        bodyText,
      };
      handleInputEmailChange(input);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editorRef.current],
  );

  const handleInputSubject = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    handleInputSubjectChange(e.target.value);
  };

  useEffect(() => {
    if (!editorRef.current) {
      return;
    }
    const hasContent = editorRef.current.getContent({ format: "text" }).trim().length > 0;
    const shouldInsertContent = !hasContent && !!inputState.body;
    const shouldClearContent =
      hasContent &&
      !inputState.body &&
      !inputState.bodyErrorMessage &&
      !inputState.subject &&
      !inputState.subjectErrorMessage;

    if (shouldClearContent) {
      isProgrammaticChange.current = true;
      editorRef.current.setContent("");
      setTimeout(() => {
        isProgrammaticChange.current = false;
      }, 0);
    } else if (shouldInsertContent) {
      isProgrammaticChange.current = true;
      editorRef.current.setContent(inputState.body);
      editorRef.current.setDirty(false);
      setTimeout(() => {
        isProgrammaticChange.current = false;
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inputState.bodyErrorMessage,
    inputState.body,
    inputState.subject,
    inputState.subjectErrorMessage,
    editorRef.current,
  ]);

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography sx={{ mt: 0, mb: 0 }} variant="h6" component={FormLabel} htmlFor="email-subject">
            Subject
            <RequiredIndicator />
          </Typography>
          <Stack
            direction="row"
            alignItems="flex-end"
            justifyContent="flex-end"
            divider={<Divider orientation="vertical" flexItem />}
            spacing={2}
            sx={{ transform: "translateY(-0.5em)" }}
          >
            <MemoEmailTemplateListDialog
              ButtonProps={{ startIcon: <DriveFolderUpload /> }}
              buttonName={"Load Email Templates"}
            />
            <MessageTemplateFields
              excludedTypes={[TemplateFieldType.Email, TemplateFieldType.Forms]}
              handleTemplateButtonClick={(_e, field) => {
                handleInputSubjectChange(`${inputState.subject} [[${field}]]`);
              }}
            />
          </Stack>
        </Stack>
        <TextField
          id="email-subject"
          name="email-subject"
          aria-describedby="subject-error-message"
          onChange={handleInputSubject}
          value={inputState.subject || ""}
          disabled={disableInput}
          error={subjectErrorMessage.length > 0}
          sx={{ width: 1 }}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Collapse in={!!subjectErrorMessage}>
            <ErrorMessage
              message={subjectErrorMessage || ""}
              sx={{ display: "block" }}
              id="subject-error-message"
            />
          </Collapse>
          <Typography
            variant="caption"
            sx={{
              fontStyle: "italic",
              color: subjectErrorMessage ? "error.main" : "primary",
              ml: "auto",
            }}
          >
            {inputState.subject?.length || 0}/{maxSubjectLength}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={(theme) => ({
          "& div[role='application']": {
            borderWidth: 1,
            borderRadius: 1,
            borderColor: bodyErrorMessage ? theme.palette.error.main : theme.palette.grey[400],
          },
        })}
      >
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ mt: 0, mb: 0 }} variant="h6" component={FormLabel} htmlFor="email-body">
            Body
            <RequiredIndicator />
          </Typography>
          <MessageTemplateFields
            excludedTypes={[TemplateFieldType.Forms]}
            handleTemplateButtonClick={(_ev, field) => {
              handleInputEmailChange(
                {
                  body: `[[${field}]]`,
                  bodyText: `[[${field}]]`,
                } as IEmailInput,
                editorRef,
                true,
              );
            }}
          />
        </Stack>
        <TinyEditorUncontrolled
          defaultValue={inputState.body || ""}
          editorRef={editorRef}
          textareaName="email-body"
          plugins={Plugins}
          toolbar={Toolbar}
          functions={Functions}
          disabled={disableInput}
          onEditorChange={onEditorChange}
        />
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-end", md: "center" },
            justifyContent: "space-between",
          }}
        >
          <Collapse in={!!bodyErrorMessage}>
            <ErrorMessage message={bodyErrorMessage || ""} sx={{ display: "block" }} />
          </Collapse>
          <Typography
            variant="caption"
            sx={{
              // CRM-485 Remove email length validation until Large Email Templates are implemented
              // display: "flex",
              display: "none",
              fontStyle: "italic",
              color: bodyErrorMessage ? "error.main" : "primary",
              ml: { xs: 0, md: "auto" },
            }}
          >
            {bodyTextOnly?.length}/{maxEmailBodyLength}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
